import React from 'react';

import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';

import AddonIcon from '@atlaskit/icon/glyph/addon';

import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import UploadIcon from '@atlaskit/icon/glyph/upload';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import CrystalBall from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/crystal-ball';
import { useIntl } from '@atlassian/jira-intl';
import { IconBoxColor, IconBox } from '../../../../../common/ui/icon-box';
import { ConfigurationItem } from '../../../../../common/ui/wizard/configuration-item';
import { WizardInfoItem } from '../../../../../common/ui/wizard/info-item';
import { Layout } from '../../../../../common/ui/wizard/layout';
import { messages as commonMessages } from '../../../../../common/ui/wizard/layout/messages';
import {
	useDeprecatedHeartbeats,
	useDeprecatedGroups,
	useDeprecatedNotificationPolicies,
} from '../../../../../services/store/deprecated-counts';
import {
	useDeprecatedIntegrations,
	useIntegrationCounts,
} from '../../../../../services/store/integrations';
import { messages } from './messages';
import type { StepThreeProps } from './types';

export const StepThree = ({ onAction, onClose }: StepThreeProps) => {
	const { formatMessage } = useIntl();

	const primaryActionButton = (
		<Button onClick={() => onAction((prevState) => prevState + 1)} appearance="primary">
			{formatMessage(commonMessages.nextButtonNonFinal)}
		</Button>
	);

	const secondaryActionButton = (
		<Button onClick={() => onAction((prevState) => prevState - 1)} appearance="default">
			{formatMessage(commonMessages.backButtonNonFinal)}
		</Button>
	);

	const [{ counts: integrationCounts }] = useIntegrationCounts();
	const [{ hasDeprecatedGlobalHeartbeats, heartbeats }] = useDeprecatedHeartbeats();
	const [{ hasDeprecatedNotificationPolicies, notificationPolicies }] =
		useDeprecatedNotificationPolicies();
	const [{ hasDeprecatedGroups, groups }] = useDeprecatedGroups();
	const [{ hasDeprecatedIntegration, deprecatedIntegrations }] = useDeprecatedIntegrations();

	return (
		<Layout
			vector={{ light: '', dark: '', component: <CrystalBall alt="" width={355} /> }}
			currentStep={3}
			actionButtons={{
				primary: primaryActionButton,
				secondary: secondaryActionButton,
			}}
			onClose={onClose}
		>
			<Flex direction="column" rowGap="space.200">
				<Flex rowGap="space.100" direction="column">
					<Heading size="large">{formatMessage(messages.titleNonFinal)}</Heading>
					<Text>{formatMessage(messages.descriptionNonFinal)}</Text>
				</Flex>
				<Stack space="space.100">
					{hasDeprecatedIntegration && (
						<WizardInfoItem
							iconBox={<IconBox icon={AddonIcon} color={IconBoxColor.Teal} withCustomContainer />}
							title={formatMessage(messages.integrationsInfoTitleNonFinal, {
								count: deprecatedIntegrations.data?.count,
							})}
							description={formatMessage(messages.integrationsInfoDescriptionNonFinal, {
								count: deprecatedIntegrations.data?.count,
								totalCount: integrationCounts.data?.totalCount,
							})}
						/>
					)}
					<WizardInfoItem
						iconBox={<IconBox icon={UploadIcon} color={IconBoxColor.Purple} withCustomContainer />}
						title={formatMessage(messages.teamsApiInfoTitleNonFinal)}
						description={formatMessage(messages.teamsApiInfoDescriptionNonFinal)}
					/>
				</Stack>
				{(hasDeprecatedGroups ||
					hasDeprecatedGlobalHeartbeats ||
					hasDeprecatedNotificationPolicies) && (
					<Stack space="space.100">
						<Heading as="h3" size="xsmall">
							{formatMessage(messages.moreConfigurationsNonFinal)}
						</Heading>
						<Flex direction="column" rowGap="space.025" xcss={configurationItemsWrapperStyles}>
							{hasDeprecatedGroups && (
								<ConfigurationItem>
									{formatMessage(messages.groupNonFinal, {
										count: groups.data?.count,
									})}
								</ConfigurationItem>
							)}
							{hasDeprecatedGlobalHeartbeats && (
								<ConfigurationItem>
									{formatMessage(messages.heartbeatsNonFinal, {
										count: heartbeats.data?.globalHeartbeatCount,
									})}
								</ConfigurationItem>
							)}
							{hasDeprecatedNotificationPolicies && (
								<ConfigurationItem>
									{formatMessage(messages.notificationPoliciesNonFinal, {
										count: notificationPolicies.data?.count,
									})}
								</ConfigurationItem>
							)}
						</Flex>
					</Stack>
				)}
				<Box paddingBlockStart="space.150">
					<Anchor
						href="https://support.atlassian.com/jira-service-management-cloud/docs/start-shifting-from-opsgenie-to-jira-service-management/#What-happens-after-starting-your-shift-from-Opsgenie"
						target="_blank"
					>
						{formatMessage(messages.learnMoreNonFinal)}
						<ShortcutIcon label="" size="small" />
					</Anchor>
				</Box>
			</Flex>
		</Layout>
	);
};

const configurationItemsWrapperStyles = xcss({
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});
